import { isEmpty } from '@infotech-soe/erp-ui-kit';
import { defineMessages, IntlFormatters } from 'react-intl';

import { NamedDomain } from '../../../../infrastructure/schemas/storage/NamedDomain';
import {
  DatasetColumnFormat,
  DatasetGridColumn,
} from '../../../../services/dataset/view-model/DatasetGridColumn';
import { DatasetGridRow } from '../../../../services/dataset/view-model/DatasetGridRow';

import numberToTimeString from './numberToTimeString';

const messages = defineMessages({
  yes: {
    id: 'ui.button.accept',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'ui.button.decline',
    defaultMessage: 'No',
  },
});

export type TDatasetCellValue = string | number | null | undefined | Date;

export type IntlFormatter = (
  format: DatasetColumnFormat,
  value: TDatasetCellValue,
  domain: NamedDomain,
  useGrouping?: boolean
) => TDatasetCellValue;

export const getFormattedValue = (
  {
    formatMessage,
    formatNumber,
    formatDate,
    formatTime,
  }: Pick<
    IntlFormatters,
    'formatMessage' | 'formatNumber' | 'formatDate' | 'formatTime'
  >,
  format: DatasetColumnFormat,
  value: TDatasetCellValue,
  domain?: NamedDomain,
  useGrouping?: boolean
): TDatasetCellValue => {
  if (value !== null && value !== undefined) {
    switch (format) {
      case DatasetColumnFormat.BOOLEAN:
        return value ? formatMessage(messages.yes) : formatMessage(messages.no);
      case DatasetColumnFormat.NUMBER:
        if (typeof value === 'number') {
          if (domain?.subtype === 'time') {
            return isEmpty(value)
              ? value
              : numberToTimeString(formatTime, value);
          } else if (domain?.subtype === 'year') {
            return formatNumber(value, {
              useGrouping: false,
            });
          } else {
            return formatNumber(value, {
              minimumFractionDigits: domain?.precision,
              maximumFractionDigits: domain?.precision,
              useGrouping: useGrouping ?? true,
            });
          }
        }
        break;
      case DatasetColumnFormat.DATE:
        return formatDate(value);
      case DatasetColumnFormat.TIME:
        return formatTime(value);
      case DatasetColumnFormat.DATE_TIME_SEC:
        return formatDate(value, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
        });
      case DatasetColumnFormat.DATE_TIME:
        return formatDate(value, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        });
      default:
        return value;
    }
  }
};

export const getFormattedActiveCellValue = (
  orderedColumns: DatasetGridColumn[],
  activeCell: number,
  rows: DatasetGridRow[],
  activeRow: number,
  intlFormatter: IntlFormatter
): string => {
  const {
    field: columnName,
    format,
    formatter,
    domain,
  } = orderedColumns[activeCell] ?? {};
  const row = rows[activeRow];

  if (!row) return '';

  const cell = row[columnName];
  const formattedValue = formatter
    ? formatter(cell, { row })
    : format === DatasetColumnFormat.NUMBER
      ? cell
      : intlFormatter(format, cell, domain);

  return formattedValue?.toString().trim() ?? '';
};
